import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppLayout } from "@/components/AppLayout";
import { AuthCallback } from "@/pages/AuthCallback";
import { Home } from "@/pages/Home";
import { Login } from "@/pages/Login";
import { Register } from "@/pages/Register";
import { ResetPassword } from "@/pages/ResetPassword";
import { NewRequest } from "@/pages/NewRequest";
import { MyRequests } from "@/pages/MyRequests";
import { RequestDetail } from "@/pages/RequestDetail";
import { Conversations } from "@/pages/Conversations";
import { ConversationDetail } from "@/pages/ConversationDetail";
import { ProDashboard } from "@/pages/ProDashboard";
import { ProFeed } from "@/pages/ProFeed";
import { ProOnboarding } from "@/pages/ProOnboarding";
import { ProSubscription } from "@/pages/ProSubscription";
import { Jobs } from "@/pages/Jobs";
import { JobDetail } from "@/pages/JobDetail";
import { Profile } from "@/pages/Profile";
import { AdminNav } from "@/pages/admin/AdminNav";
import { AdminDashboard } from "@/pages/admin/AdminDashboard";
import { AdminProfessionals } from "@/pages/admin/AdminProfessionals";
import { AdminUsers } from "@/pages/admin/AdminUsers";
import { AdminRequests } from "@/pages/admin/AdminRequests";
import { AdminSubscriptions } from "@/pages/admin/AdminSubscriptions";
import { AdminReports } from "@/pages/admin/AdminReports";
import { Loader2, ShieldAlert } from "lucide-react";

const FullScreenLoader = () => (
  <div className="min-h-screen flex items-center justify-center" data-testid="app-loading">
    <Loader2 className="w-8 h-8 animate-spin text-whoo" />
  </div>
);

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return children;
}

function ProRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (user.role !== "professional" && user.role !== "admin") return <Navigate to="/home" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (user.role !== "admin") {
    return (
      <div className="px-4 pt-16 text-center space-y-3" data-testid="admin-access-denied">
        <ShieldAlert className="w-10 h-10 text-red-400 mx-auto" />
        <h1 className="font-heading text-xl font-bold text-slate-900">Accesso negato</h1>
        <p className="text-sm text-slate-600">Quest'area è riservata agli amministratori di WhooNow.</p>
      </div>
    );
  }
  return (
    <>
      <AdminNav />
      {children}
    </>
  );
}

function AppRoutes() {
  const location = useLocation();
  // Gestione callback OAuth: il session_id va processato PRIMA di qualsiasi rotta protetta
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/home" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/richieste/nuova" element={<Protected><NewRequest /></Protected>} />
        <Route path="/richieste" element={<Protected><MyRequests /></Protected>} />
        <Route path="/richieste/:requestId" element={<Protected><RequestDetail /></Protected>} />
        <Route path="/chat" element={<Protected><Conversations /></Protected>} />
        <Route path="/chat/:convId" element={<Protected><ConversationDetail /></Protected>} />
        <Route path="/pro" element={<ProRoute><ProDashboard /></ProRoute>} />
        <Route path="/pro/feed" element={<ProRoute><ProFeed /></ProRoute>} />
        <Route path="/pro/onboarding" element={<ProRoute><ProOnboarding /></ProRoute>} />
        <Route path="/pro/abbonamento" element={<ProRoute><ProSubscription /></ProRoute>} />
        <Route path="/lavori" element={<Protected><Jobs /></Protected>} />
        <Route path="/lavori/:jobId" element={<Protected><JobDetail /></Protected>} />
        <Route path="/profilo" element={<Protected><Profile /></Protected>} />
        <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="/admin/professionisti" element={<AdminRoute><AdminProfessionals /></AdminRoute>} />
        <Route path="/admin/utenti" element={<AdminRoute><AdminUsers /></AdminRoute>} />
        <Route path="/admin/richieste" element={<AdminRoute><AdminRequests /></AdminRoute>} />
        <Route path="/admin/abbonamenti" element={<AdminRoute><AdminSubscriptions /></AdminRoute>} />
        <Route path="/admin/segnalazioni" element={<AdminRoute><AdminReports /></AdminRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="bottom-center" richColors offset="88px" />
      </AuthProvider>
    </BrowserRouter>
  );
}
